/**
 * Chain definition and read-only client for browser code.
 *
 * Split from `lib/chain.ts` so client components never pull in the server's
 * RPC list, which may contain a private, keyed endpoint.
 */
import { createPublicClient, fallback, http } from "viem";
import { bsc, bscTestnet, hardhat } from "viem/chains";

import { BSC_TESTNET_CHAIN_ID, CHAIN_ID, LOCAL_CHAIN_ID } from "./config";
import {
  BSC_MAINNET_RPC_URLS,
  BSC_TESTNET_RPC_URLS,
  LOCAL_RPC_URLS,
} from "./rpc-endpoints";

export const chain =
  CHAIN_ID === LOCAL_CHAIN_ID ? hardhat : CHAIN_ID === BSC_TESTNET_CHAIN_ID ? bscTestnet : bsc;

/** Public endpoints only. Anything sensitive stays server-side. */
export const walletRpcUrls =
  CHAIN_ID === LOCAL_CHAIN_ID
    ? LOCAL_RPC_URLS
    : CHAIN_ID === BSC_TESTNET_CHAIN_ID
      ? BSC_TESTNET_RPC_URLS
      : BSC_MAINNET_RPC_URLS;

/**
 * `/api/rpc` first, public endpoints behind it.
 *
 * The relay is the reliable path: it fails over across the server's whole list,
 * including a paid endpoint when one is configured, and needs no third-party
 * CORS. The direct endpoints stay as a second line so a portal server that is
 * itself struggling — or rate-limiting the client — still leaves the claim
 * button working.
 *
 * `undefined` while server-rendering: this module is evaluated during SSR too,
 * and a relative URL has no meaning to `fetch` in Node. Nothing calls the
 * client on the server, so the browser-only entry is simply omitted there.
 */
const relayUrl =
  typeof window === "undefined" ? undefined : new URL("/api/rpc", window.location.origin).toString();

/** No per-endpoint retries: with several endpoints available, rotating to the
 *  next one beats asking a sick node the same question twice. */
const directTransports = walletRpcUrls.map((url) =>
  http(url, { timeout: 8_000, retryCount: 0 }),
);

/** Shared with the wagmi config in `lib/wagmi.ts`, so wallet-side reads take the
 *  same relay-first path as the portal's own. */
export const browserTransport = fallback(
  relayUrl
    ? [http(relayUrl, { timeout: 15_000, retryCount: 0 }), ...directTransports]
    : directTransports,
  { rank: false, retryCount: 1 },
);

export const browserPublicClient = createPublicClient({
  chain,
  transport: browserTransport,
});
